import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { history } from '../../_utils';
import {
    matchesOperatorQuery,
    operatorName,
    operatorCode,
    shiftKindKey,
    isWorkShift,
    shiftHours,
    buildShiftMap,
    shiftMapOperatorIds,
} from '../utils';
import ScheduleCell from './ScheduleCell';
import SummaryRow from './SummaryRow';

const dayNames = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

const WeekView = ({
    operators,
    schedule,
    weekDates,
    onCellClick,
    searchQuery = '',
    holidays = [],
    onOperatorClick,
    kindFilter = [],
    hideEmpty = true,
}) => {
    const [unfolded, setUnfolded] = useState(false);

    useEffect(() => {
        setUnfolded(false);
    }, [hideEmpty, searchQuery, weekDates]);

    const shiftMap = useMemo(() => buildShiftMap(schedule), [schedule]);
    const periodIds = useMemo(() => shiftMapOperatorIds(shiftMap), [shiftMap]);

    const filteredOperators = useMemo(
        () => (operators || []).filter((op) => matchesOperatorQuery(op, searchQuery)),
        [operators, searchQuery]
    );

    const rows = useMemo(() => {
        const withShifts = [];
        const empty = [];
        filteredOperators.forEach((operator) => {
            const id = operator.id || operator._id;
            const shifts = weekDates.map((date) => shiftMap[`${id}|${moment(date).format('YYYY-MM-DD')}`]);
            const hours = shifts.reduce((sum, shift) => sum + shiftHours(shift), 0);
            const days = shifts.filter(isWorkShift).length;
            const hasShift = periodIds.has(String(id));
            const row = { operator, shifts, hours, days, hasShift };
            if (hasShift) withShifts.push(row);
            else empty.push(row);
        });
        return { withShifts, empty };
    }, [filteredOperators, shiftMap, periodIds, weekDates]);

    const foldEmpty = hideEmpty && !unfolded && rows.empty.length > 0 && rows.withShifts.length > 0;
    const visibleRows = foldEmpty ? rows.withShifts : rows.withShifts.concat(rows.empty);

    const handleDateClick = (date) => {
        const formattedDate = moment(date).format('YYYY-MM-DD');
        try {
            const currentSearch = history.location.search;
            if (currentSearch) {
                sessionStorage.setItem('schedule_filter', currentSearch.replace(/^\?/, ''));
            }
        } catch (error) {
            console.error('Error saving filter:', error);
        }
        history.push(`/schedule/${formattedDate}/show`);
    };

    return (
        <table className="pc-sched-table">
            <thead>
                <tr>
                    <th className="namecell">Сотрудник</th>
                    {weekDates.map((date) => {
                        const isWeekend = moment(date).isoWeekday() >= 6;
                        const holiday = holidays.find((item) =>
                            moment(item.date).format('YYYY-MM-DD') === moment(date).format('YYYY-MM-DD')
                        );
                        const isHoliday = holiday && holiday.type === 'holiday';
                        return (
                            <th
                                key={moment(date).format('YYYY-MM-DD')}
                                className={`dayhead${isWeekend || isHoliday ? ' we' : ''}`}
                                onClick={() => handleDateClick(date)}
                                title={holiday ? holiday.name : 'Открыть день'}
                            >
                                {dayNames[moment(date).isoWeekday() - 1]} {moment(date).date()}
                            </th>
                        );
                    })}
                    <th>Σ час</th>
                </tr>
                <SummaryRow schedule={schedule} weekDates={weekDates} />
            </thead>
            <tbody>
                {visibleRows.map((row) => {
                    const id = row.operator.id || row.operator._id;
                    return (
                        <tr key={id}>
                            <td className="namecell">
                                <button
                                    type="button"
                                    className="nm"
                                    title={operatorName(row.operator)}
                                    onClick={() => onOperatorClick && onOperatorClick(id)}
                                >
                                    {operatorName(row.operator)}
                                </button>
                                <div className="cd">{operatorCode(row.operator)}{row.days ? ` · ${row.days} см` : ''}</div>
                            </td>
                            {weekDates.map((date, index) => {
                                const shift = row.shifts[index];
                                const key = shiftKindKey(shift);
                                const dimmed = kindFilter.length > 0 && key && !kindFilter.includes(key);
                                return (
                                    <td className="pc-sched-cell" key={moment(date).format('YYYY-MM-DD')}>
                                        <ScheduleCell
                                            shift={shift}
                                            dimmed={dimmed}
                                            onClick={() => onCellClick && onCellClick(row.operator, date, shift)}
                                        />
                                    </td>
                                );
                            })}
                            <td className="pc-sched-hours">
                                {row.hours ? <b>{row.hours}</b> : <span className="d">—</span>}
                            </td>
                        </tr>
                    );
                })}
                {foldEmpty && (
                    <tr className="pc-sched-fold">
                        <td colSpan={weekDates.length + 2}>
                            <button type="button" onClick={() => setUnfolded(true)}>
                                Ещё {rows.empty.length} без смен в этом месяце — показать
                                <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7">
                                    <path d="M4 6.5L8 10.5l4-4" />
                                </svg>
                            </button>
                        </td>
                    </tr>
                )}
            </tbody>
        </table>
    );
};

export default WeekView;
